import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  useToast,
} from "@repo/ui";
import { X, Edit2, Check, Loader2, Plus, Trash2 } from "lucide-react";
import { cn } from "@repo/utils";
import type { Wholesaler } from "@/lib/types/inventory";
import {
  getWholesalers,
  createWholesaler,
  updateWholesaler,
  deleteWholesaler,
} from "@/lib/api/wholesalers";
import { ApiClientError } from "@/lib/api/client";
import { useApproval } from "@/context/approval-context";
import { handlePermissionError } from "@/lib/utils/handle-permission-error";

export interface WholesalerManagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onWholesalersChange?: () => void;
}

export function WholesalerManagementModal({
  open,
  onOpenChange,
  onWholesalersChange,
}: WholesalerManagementModalProps) {
  const { toast } = useToast();
  const { requestApproval } = useApproval();
  const [wholesalers, setWholesalers] = React.useState<Wholesaler[]>([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editValue, setEditValue] = React.useState("");
  const [isSaving, setIsSaving] = React.useState(false);
  const [deletingId, setDeletingId] = React.useState<string | null>(null);
  const [isAdding, setIsAdding] = React.useState(false);
  const [newWholesalerName, setNewWholesalerName] = React.useState("");

  // Load wholesalers
  const loadWholesalers = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await getWholesalers();
      setWholesalers(data.data.wholesalers || []);
    } catch (error) {
      console.error("Load error:", error);
      toast({
        title: "Error",
        description:
          error instanceof ApiClientError
            ? error.message
            : "Failed to load wholesalers. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // Load on open
  React.useEffect(() => {
    if (open) {
      loadWholesalers();
    }
  }, [open, loadWholesalers]);

  // Start editing
  const handleStartEdit = (wholesaler: Wholesaler) => {
    setEditingId(wholesaler.id);
    setEditValue(wholesaler.name);
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditValue("");
  };

  // Save edit
  const handleSaveEdit = async (id: string) => {
    if (!editValue.trim()) {
      toast({
        title: "Error",
        description: "Wholesaler name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const data = await updateWholesaler(id, { name: editValue.trim() });

      // Update in list
      setWholesalers((prev) =>
        prev
          .map((w) =>
            w.id === id ? { ...w, name: data.data.wholesaler.name } : w
          )
          .sort((a, b) => a.name.localeCompare(b.name))
      );

      setEditingId(null);
      setEditValue("");
      onWholesalersChange?.();

      toast({
        title: "Wholesaler Updated",
        description: `"${data.data.wholesaler.name}" has been updated.`,
        variant: "success",
      });
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {},
          actionDescription: "Update wholesaler",
        })
      )
        return;
      console.error("Update error:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to update wholesaler",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Delete wholesaler
  const handleDelete = async (id: string, name: string, usageCount: number) => {
    if (usageCount > 0) {
      toast({
        title: "Cannot Delete",
        description: `"${name}" is used in ${usageCount} receipt${usageCount !== 1 ? "s" : ""} and cannot be deleted.`,
        variant: "destructive",
      });
      return;
    }

    setDeletingId(id);
    try {
      await deleteWholesaler(id);

      // Remove from list
      setWholesalers((prev) => prev.filter((w) => w.id !== id));
      onWholesalersChange?.();

      toast({
        title: "Wholesaler Deleted",
        description: `"${name}" has been removed.`,
        variant: "success",
      });
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {},
          actionDescription: "Delete wholesaler",
        })
      )
        return;
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to delete wholesaler",
        variant: "destructive",
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Add new wholesaler
  const handleAddNew = async () => {
    if (!newWholesalerName.trim()) {
      toast({
        title: "Error",
        description: "Wholesaler name cannot be empty",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);
    try {
      const data = await createWholesaler({ name: newWholesalerName.trim() });

      const newWholesaler = data.data.wholesaler;

      // Add to list
      setWholesalers((prev) =>
        [...prev, newWholesaler].sort((a, b) => a.name.localeCompare(b.name))
      );

      setNewWholesalerName("");
      setIsAdding(false);
      onWholesalersChange?.();

      toast({
        title: "Wholesaler Created",
        description: `"${newWholesaler.name}" has been added.`,
        variant: "success",
      });
    } catch (error) {
      if (
        error instanceof ApiClientError &&
        handlePermissionError(error, {
          requestApproval,
          toast,
          retryFn: async () => {},
          actionDescription: "Create wholesaler",
        })
      )
        return;
      console.error("Create error:", error);
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "Failed to create wholesaler",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Manage Wholesalers</DialogTitle>
          <DialogDescription>
            Add, edit, or delete wholesalers. Wholesalers in use cannot be
            deleted.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin" />
              <span className="ml-2 text-sm text-muted-foreground">
                Loading wholesalers...
              </span>
            </div>
          )}

          {!isLoading && (
            <>
              {/* Wholesalers List */}
              {wholesalers.length > 0 ? (
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">
                    Your wholesalers ({wholesalers.length})
                  </div>
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {wholesalers.map((wholesaler) => (
                      <div
                        key={wholesaler.id}
                        className="flex items-center gap-2 rounded-md border border-border p-3"
                      >
                        {editingId === wholesaler.id ? (
                          <>
                            <Input
                              value={editValue}
                              onChange={(e) => setEditValue(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  handleSaveEdit(wholesaler.id);
                                } else if (e.key === "Escape") {
                                  handleCancelEdit();
                                }
                              }}
                              className="flex-1"
                              autoFocus
                              disabled={isSaving}
                            />
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleSaveEdit(wholesaler.id)}
                              disabled={isSaving}
                            >
                              {isSaving ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={handleCancelEdit}
                              disabled={isSaving}
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        ) : (
                          <>
                            <div className="flex-1 min-w-0">
                              <div className="font-medium truncate">
                                {wholesaler.name}
                              </div>
                              {wholesaler.usage_count !== undefined && (
                                <div className="text-xs text-muted-foreground">
                                  {wholesaler.usage_count > 0
                                    ? `Used ${wholesaler.usage_count} time${wholesaler.usage_count !== 1 ? "s" : ""}`
                                    : "Not used"}
                                </div>
                              )}
                            </div>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStartEdit(wholesaler)}
                              disabled={deletingId === wholesaler.id}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              onClick={() =>
                                handleDelete(
                                  wholesaler.id,
                                  wholesaler.name,
                                  wholesaler.usage_count || 0
                                )
                              }
                              disabled={
                                (wholesaler.usage_count || 0) > 0 ||
                                deletingId === wholesaler.id
                              }
                              className={cn(
                                (wholesaler.usage_count || 0) > 0 &&
                                  "opacity-50 cursor-not-allowed"
                              )}
                            >
                              {deletingId === wholesaler.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-muted-foreground">
                  No wholesalers yet. Add your first one below.
                </div>
              )}

              {/* Add New Section */}
              <div className="border-t border-border pt-4">
                {isAdding ? (
                  <div className="flex items-center gap-2">
                    <Input
                      value={newWholesalerName}
                      onChange={(e) => setNewWholesalerName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleAddNew();
                        } else if (e.key === "Escape") {
                          setIsAdding(false);
                          setNewWholesalerName("");
                        }
                      }}
                      placeholder="Enter wholesaler name"
                      className="flex-1"
                      autoFocus
                      disabled={isSaving}
                    />
                    <Button
                      type="button"
                      size="sm"
                      onClick={handleAddNew}
                      disabled={isSaving}
                    >
                      {isSaving ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Check className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setIsAdding(false);
                        setNewWholesalerName("");
                      }}
                      disabled={isSaving}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full"
                    onClick={() => setIsAdding(true)}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Wholesaler
                  </Button>
                )}
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
